import { useRouter, useLocation, useParams } from '@tanstack/react-router';
import { ParsedQuery } from 'query-string';
import { useCallback } from 'react';

export const useRouterHooks = <T extends Record<string, any> = any>() => {
  const router = useRouter();
  const location = useLocation();
  const params = useParams({ strict: false }) as T;

  return {
    match: { params },
    location,
    history: {
      push: (to: string, search?: Record<string, any>) =>
        router.navigate({
          to,
          search: search ? () => search : undefined,
        }),
      replace: (to: string, search?: Record<string, any>) =>
        router.navigate({
          to,
          search: search ? () => search : undefined,
          replace: true,
        }),
      goBack: () => window.history.back(),
    },
  };
};

export const useQuery = <T extends ParsedQuery = ParsedQuery>(): T => {
  const location = useLocation();
  const query = location.search;
  return query as T;
};

interface UpdateQueryOptions {
  replace?: boolean;
}

export const useUpdateQuery = <T extends ParsedQuery>(
  options: UpdateQueryOptions = {}
): ((patch: Partial<T>) => void) => {
  const router = useRouter();
  const query = useQuery<T>();

  return useCallback(
    (patch: Partial<T>): void => {
      const newQuery = { ...query, ...patch };
      router.navigate({
        search: (() => newQuery) as any,
        replace: options.replace,
      });
    },
    [router, query, options.replace]
  );
};
